import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink, useHistory, useLocation } from 'react-router-dom';
import Table from 'react-bootstrap/Table';
import { Card, Spinner, Row, Col } from 'react-bootstrap';
import { toast } from 'react-toastify';

import {
  baoCaoGetTongHopNhanVienAction,
  baoCaoClearTongHopNhanVien,
  baoCaoGetChiTietPhatTrienPhanMemAction
} from '@app/store/reducers/baocao';

import { URI_INTERNAL } from '@app/utils/pathLocations';
import { rangeDateReport, messageToast } from '@app/utils/define';

const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const getErrMsg = (error) => {
  if (!error) return '';
  let msg =
    (error && error.message) ||
    (typeof error === 'string' ? error : '') ||
    (error && error.title) ||
    '';

  if (!msg && error && error.errors) {
    msg = Object.values(error.errors).flat().join('; ');
  }
  return msg;
};

const RANGE_CUSTOM = 'Custom Range';
const DEFAULT_RANGE_LABEL = '7 ngày trước';
const RANGE_QUERY_EMPTY = '__EMPTY__';

const RANGE_OPTIONS = [
  'Hôm nay',
  'Hôm qua',
  '7 ngày trước',
  '30 ngày trước',
  'Tháng này',
  'Tháng trước',
  '3 tháng trước',
  '6 tháng trước',
  'Năm',
  RANGE_CUSTOM,
];

const MODULES = [
  { value: 'ALL', label: 'Tất cả module' },
  { value: 'PHAN_MEM', label: 'Phát triển phần mềm' },
  { value: 'XU_LY_YEU_CAU', label: 'Xử lý yêu cầu' },
  { value: 'HO_TRO', label: 'Hỗ trợ' },
  { value: 'NHIEM_VU_KHAC', label: 'Nhiệm vụ khác' }
];

const toYMD = (d) => {
  if (!d) return '';
  const dt = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dt.getTime())) return '';
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const day = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const fmtDDMMYYYY = (ymd) => {
  if (!ymd) return '';
  if (typeof ymd === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(ymd)) {
    const [y, m, d] = ymd.split('-');
    return `${d}/${m}/${y}`;
  }
  const dt = new Date(ymd);
  if (Number.isNaN(dt.getTime())) return '';
  return dt.toLocaleDateString('en-GB');
};

const getRangeFromTo = (label) => {
  if (!label || label === RANGE_CUSTOM) return { from: '', to: '' };

  const pair = rangeDateReport?.[label];
  if (!Array.isArray(pair) || pair.length !== 2) return { from: '', to: '' };

  return {
    from: toYMD(pair[0]),
    to: toYMD(pair[1]),
  };
};

const getDefaultRangeState = () => {
  const { from, to } = getRangeFromTo(DEFAULT_RANGE_LABEL);

  return {
    rangeLabel: DEFAULT_RANGE_LABEL,
    appliedRangeLabel: DEFAULT_RANGE_LABEL,
    fromDate: from,
    toDate: to,
    appliedFrom: from,
    appliedTo: to,
  };
};

const getRangeStateFromSearch = (search = '') => {
  const sp = new URLSearchParams(search || '');
  const hasRangeState = sp.has('RangeLabel') || sp.has('From') || sp.has('To');

  if (!hasRangeState) return null;

  const rawLabel = sp.get('RangeLabel') || '';
  const from = sp.get('From') || '';
  const to = sp.get('To') || '';

  if (rawLabel === RANGE_QUERY_EMPTY) {
    return {
      rangeLabel: '',
      appliedRangeLabel: '',
      fromDate: '',
      toDate: '',
      appliedFrom: '',
      appliedTo: '',
    };
  }

  const normalizedLabel = rawLabel || ((from || to) ? RANGE_CUSTOM : '');

  return {
    rangeLabel: normalizedLabel,
    appliedRangeLabel: normalizedLabel,
    fromDate: from,
    toDate: to,
    appliedFrom: from,
    appliedTo: to,
  };
};

const BaoCaoTongHopNhanVien = ({
  commonFrom = '',
  commonTo = '',
  commonRangeLabel = '',
  commonVersion = 0,
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  const queryTeamFilter = useMemo(() => {
    const sp = new URLSearchParams(location.search || '');
    const value = sp.get('filterTeamId') || '';
    if (value === '0') return '';
    return value;
  }, [location.search]);

  const queryModuleFilter = useMemo(() => {
    const sp = new URLSearchParams(location.search || '');
    return sp.get('moduleType') || 'ALL';
  }, [location.search]);

  const [teamFilter, setTeamFilter] = useState(queryTeamFilter);
  const [moduleFilter, setModuleFilter] = useState(queryModuleFilter);

  useEffect(() => {
    setTeamFilter(queryTeamFilter);
  }, [queryTeamFilter]);

  useEffect(() => {
    setModuleFilter(queryModuleFilter);
  }, [queryModuleFilter]);

  const replaceSearchParams = (mutator) => {
    const sp = new URLSearchParams(location.search || '');
    mutator(sp);
    const qs = sp.toString();
    history.replace({
      pathname: location.pathname,
      search: qs ? `?${qs}` : '',
    });
  };

  const updateTeamFilterQuery = (nextTeamFilter = '') => {
    replaceSearchParams((sp) => {
      if (!nextTeamFilter) sp.delete('filterTeamId');
      else sp.set('filterTeamId', String(nextTeamFilter));
    });
  };

  const updateModuleFilterQuery = (nextModuleFilter = 'ALL') => {
    replaceSearchParams((sp) => {
      if (nextModuleFilter === 'ALL') sp.delete('moduleType');
      else sp.set('moduleType', nextModuleFilter);
    });
  };

  const updateRangeFilterQuery = ({
    nextRangeLabel = '',
    nextAppliedRangeLabel = '',
    nextAppliedFrom = '',
    nextAppliedTo = '',
  }) => {
    replaceSearchParams((sp) => {
      sp.delete('RangeLabel');
      sp.delete('From');
      sp.delete('To');

      const normalizedLabel = nextAppliedRangeLabel || nextRangeLabel || '';

      if (!normalizedLabel && !nextAppliedFrom && !nextAppliedTo) {
        sp.set('RangeLabel', RANGE_QUERY_EMPTY);
        return;
      }

      if (normalizedLabel) sp.set('RangeLabel', normalizedLabel);
      if (nextAppliedFrom) sp.set('From', nextAppliedFrom);
      if (nextAppliedTo) sp.set('To', nextAppliedTo);
    });
  };

  const returnUrl = useMemo(
    () => encodeURIComponent(`${location.pathname}${location.search || ''}`),
    [location.pathname, location.search]
  );

  const {
    tongHopNhanVien,
    loadingTongHopNhanVien,
    errorTongHopNhanVien,
    chiTietPhatTrienPhanMem,
    loadingChiTietPhatTrienPhanMem
  } = useSelector((s) => s.baocao || {});

  const queryRangeState = useMemo(
    () => getRangeStateFromSearch(location.search || ''),
    [location.search]
  );

  const hasQueryRangeState = !!queryRangeState;
  const defaultRangeState = useMemo(() => getDefaultRangeState(), []);

  const externalCommonRangeState = useMemo(() => {
    const hasExternal =
      commonVersion > 0 || !!commonRangeLabel || !!commonFrom || !!commonTo;

    if (!hasExternal) return null;

    const normalizedLabel =
      commonRangeLabel || ((commonFrom || commonTo) ? RANGE_CUSTOM : '');

    return {
      rangeLabel: normalizedLabel,
      appliedRangeLabel: normalizedLabel,
      fromDate: commonFrom || '',
      toDate: commonTo || '',
      appliedFrom: commonFrom || '',
      appliedTo: commonTo || '',
    };
  }, [commonVersion, commonRangeLabel, commonFrom, commonTo]);

  const initialRangeState =
    queryRangeState || externalCommonRangeState || defaultRangeState;

  const [rangeLabel, setRangeLabel] = useState(initialRangeState.rangeLabel);
  const [appliedRangeLabel, setAppliedRangeLabel] = useState(
    initialRangeState.appliedRangeLabel
  );

  const [fromDate, setFromDate] = useState(initialRangeState.fromDate);
  const [toDate, setToDate] = useState(initialRangeState.toDate);
  const [appliedFrom, setAppliedFrom] = useState(initialRangeState.appliedFrom);
  const [appliedTo, setAppliedTo] = useState(initialRangeState.appliedTo);

  useEffect(() => {
    if (!hasQueryRangeState) return;
    setRangeLabel(queryRangeState.rangeLabel);
    setAppliedRangeLabel(queryRangeState.appliedRangeLabel);
    setFromDate(queryRangeState.fromDate);
    setToDate(queryRangeState.toDate);
    setAppliedFrom(queryRangeState.appliedFrom);
    setAppliedTo(queryRangeState.appliedTo);
  }, [hasQueryRangeState, queryRangeState]);

  useEffect(() => {
    if (hasQueryRangeState) return;
    if (!externalCommonRangeState) return;
    setRangeLabel(externalCommonRangeState.rangeLabel);
    setAppliedRangeLabel(externalCommonRangeState.appliedRangeLabel);
    setFromDate(externalCommonRangeState.fromDate);
    setToDate(externalCommonRangeState.toDate);
    setAppliedFrom(externalCommonRangeState.appliedFrom);
    setAppliedTo(externalCommonRangeState.appliedTo);
  }, [hasQueryRangeState, externalCommonRangeState]);

  useEffect(() => {
    dispatch(
      baoCaoGetTongHopNhanVienAction({
        From: appliedFrom || '',
        To: appliedTo || '',
      })
    );

    if (moduleFilter === 'PHAN_MEM' || moduleFilter === 'ALL') {
      dispatch(
        baoCaoGetChiTietPhatTrienPhanMemAction({
          teamId: teamFilter || null,
          From: appliedFrom || '',
          To: appliedTo || '',
        })
      );
    }

    return () => {
      dispatch(baoCaoClearTongHopNhanVien());
    };
  }, [dispatch, appliedFrom, appliedTo, teamFilter, moduleFilter]);

  useEffect(() => {
    if (!errorTongHopNhanVien) return;
    toast.error(
      getErrMsg(errorTongHopNhanVien) || 'Không thể tải dữ liệu báo cáo chi tiết nhân viên.'
    );
  }, [errorTongHopNhanVien]);

  const handleChangeRange = (e) => {
    const v = e.target.value || '';
    setRangeLabel(v);

    if (!v) {
      setFromDate('');
      setToDate('');
      setAppliedFrom('');
      setAppliedTo('');
      setAppliedRangeLabel('');
      updateRangeFilterQuery({
        nextRangeLabel: '',
        nextAppliedRangeLabel: '',
        nextAppliedFrom: '',
        nextAppliedTo: '',
      });
      return;
    }

    if (v !== RANGE_CUSTOM) {
      const { from, to } = getRangeFromTo(v);
      setFromDate(from);
      setToDate(to);
      setAppliedFrom(from);
      setAppliedTo(to);
      setAppliedRangeLabel(v);
      updateRangeFilterQuery({
        nextRangeLabel: v,
        nextAppliedRangeLabel: v,
        nextAppliedFrom: from,
        nextAppliedTo: to,
      });
      return;
    }

    setFromDate(appliedFrom || '');
    setToDate(appliedTo || '');
  };

  const handleApplyCustomRange = () => {
    if (rangeLabel === RANGE_CUSTOM && !fromDate && !toDate) {
      toast.error(messageToast?.messageToastChooseDateError || 'Mời chọn ngày');
      return;
    }
    let f = fromDate || '';
    let t = toDate || '';
    if (f && t && t < f) {
      const tmp = f;
      f = t;
      t = tmp;
    }
    setAppliedFrom(f);
    setAppliedTo(t);
    setAppliedRangeLabel(RANGE_CUSTOM);
    updateRangeFilterQuery({
      nextRangeLabel: RANGE_CUSTOM,
      nextAppliedRangeLabel: RANGE_CUSTOM,
      nextAppliedFrom: f,
      nextAppliedTo: t,
    });
  };

  const handleChangeTeamFilter = (e) => {
    const v = e.target.value || '';
    setTeamFilter(v);
    updateTeamFilterQuery(v);
  };

  const handleClearFilter = () => {
    setRangeLabel('');
    setFromDate('');
    setToDate('');
    setAppliedRangeLabel('');
    setAppliedFrom('');
    setAppliedTo('');
    setTeamFilter('');
    setModuleFilter('ALL');

    replaceSearchParams((sp) => {
      sp.delete('filterTeamId');
      sp.delete('moduleType');
      sp.delete('RangeLabel');
      sp.delete('From');
      sp.delete('To');
      sp.set('RangeLabel', RANGE_QUERY_EMPTY);
    });
  };

  const rows = useMemo(() => {
    if (!Array.isArray(tongHopNhanVien)) return [];

    return tongHopNhanVien.map((x) => ({
      nhanVienId: x?.nhanVienId ?? x?.NhanVienId ?? 0,
      nguoiDungId: x?.nguoiDungId ?? x?.NguoiDungId ?? '',
      teamId: x?.teamId ?? x?.TeamId ?? null,
      tenTeam: x?.tenTeam ?? x?.TenTeam ?? '',
      tenThanhVien: x?.tenThanhVien ?? x?.TenThanhVien ?? '',
      tongCongViec: toNum(x?.tongCongViec ?? x?.TongCongViec ?? 0),
      hoanThanh: toNum(x?.hoanThanh ?? x?.HoanThanh ?? 0),
      dangThucHien: toNum(x?.dangThucHien ?? x?.DangThucHien ?? 0),

      phanMem: toNum(x?.phanMem ?? x?.PhanMem ?? 0),
      phanMemHoanThanh: toNum(x?.phanMemHoanThanh ?? x?.PhanMemHoanThanh ?? 0),
      phanMemDangThucHien: toNum(x?.phanMemDangThucHien ?? x?.PhanMemDangThucHien ?? 0),

      xuLyYeuCau: toNum(x?.xuLyYeuCau ?? x?.XuLyYeuCau ?? 0),
      xuLyYeuCauHoanThanh: toNum(x?.xuLyYeuCauHoanThanh ?? x?.XuLyYeuCauHoanThanh ?? 0),
      xuLyYeuCauDangThucHien: toNum(x?.xuLyYeuCauDangThucHien ?? x?.XuLyYeuCauDangThucHien ?? 0),

      hoTro: toNum(x?.hoTro ?? x?.HoTro ?? 0),
      hoTroHoanThanh: toNum(x?.hoTroHoanThanh ?? x?.HoTroHoanThanh ?? 0),
      hoTroDangThucHien: toNum(x?.hoTroDangThucHien ?? x?.HoTroDangThucHien ?? 0),

      nhiemVuKhac: toNum(x?.nhiemVuKhac ?? x?.NhiemVuKhac ?? 0),
      nhiemVuKhacHoanThanh: toNum(x?.nhiemVuKhacHoanThanh ?? x?.NhiemVuKhacHoanThanh ?? 0),
      nhiemVuKhacDangThucHien: toNum(x?.nhiemVuKhacDangThucHien ?? x?.NhiemVuKhacDangThucHien ?? 0),
    }));
  }, [tongHopNhanVien]);

  const teamOptions = useMemo(() => {
    const seen = new Map();
    rows.forEach((r) => {
      if (r.teamId == null || r.teamId === '') return;
      const value = String(r.teamId);
      const label = r.tenTeam && String(r.tenTeam).trim();
      if (!label || value === '0' || label.toLowerCase() === 'chưa gán nhóm') return;
      if (!seen.has(value)) seen.set(value, label);
    });
    return Array.from(seen.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => String(a.label).localeCompare(String(b.label), 'vi'));
  }, [rows]);

  const filteredRows = useMemo(() => {
    let rs = rows;
    if (teamFilter) rs = rs.filter((r) => String(r.teamId) === String(teamFilter));
    return rs;
  }, [rows, teamFilter]);

  // Aggregation for Dashboard stats
  const dashboardStats = useMemo(() => {
    let _totalTasks = 0, _totalDone = 0, _totalDoing = 0;

    if (moduleFilter === 'PHAN_MEM') {
      _totalTasks = filteredRows.reduce((a, b) => a + b.phanMem, 0);
      _totalDone = filteredRows.reduce((a, b) => a + b.phanMemHoanThanh, 0);
      _totalDoing = filteredRows.reduce((a, b) => a + b.phanMemDangThucHien, 0);
    } else if (moduleFilter === 'XU_LY_YEU_CAU') {
      _totalTasks = filteredRows.reduce((a, b) => a + b.xuLyYeuCau, 0);
      _totalDone = filteredRows.reduce((a, b) => a + b.xuLyYeuCauHoanThanh, 0);
      _totalDoing = filteredRows.reduce((a, b) => a + b.xuLyYeuCauDangThucHien, 0);
    } else if (moduleFilter === 'HO_TRO') {
      _totalTasks = filteredRows.reduce((a, b) => a + b.hoTro, 0);
      _totalDone = filteredRows.reduce((a, b) => a + b.hoTroHoanThanh, 0);
      _totalDoing = filteredRows.reduce((a, b) => a + b.hoTroDangThucHien, 0);
    } else if (moduleFilter === 'NHIEM_VU_KHAC') {
      _totalTasks = filteredRows.reduce((a, b) => a + b.nhiemVuKhac, 0);
      _totalDone = filteredRows.reduce((a, b) => a + b.nhiemVuKhacHoanThanh, 0);
      _totalDoing = filteredRows.reduce((a, b) => a + b.nhiemVuKhacDangThucHien, 0);
    } else {
      _totalTasks = filteredRows.reduce((a, b) => a + b.tongCongViec, 0);
      _totalDone = filteredRows.reduce((a, b) => a + b.hoanThanh, 0);
      _totalDoing = filteredRows.reduce((a, b) => a + b.dangThucHien, 0);
    }

    const progress = _totalTasks > 0 ? Math.round((_totalDone / _totalTasks) * 100) : 0;

    return {
      totalEmployees: filteredRows.length,
      totalTasks: _totalTasks,
      totalDone: _totalDone,
      totalDoing: _totalDoing,
      progress
    };
  }, [filteredRows, moduleFilter]);

  const detailPath = URI_INTERNAL.BAOCAO_CHI_TIET_NHAN_VIEN_PATH || `${URI_INTERNAL.BAOCAO_THONGKE_PATH}/chi-tiet-nhan-vien`;

  const summaryCellTdStyle = {
    verticalAlign: 'middle',
  };

  const moduleCellTdStyle = {
    paddingTop: '6px',
    paddingBottom: '6px',
    paddingLeft: '6px',
    paddingRight: '6px',
    verticalAlign: 'middle',
  };

  const renderModuleStatusCell = (done, doing) => {
    const total = done + doing;
    const pct = total > 0 ? Math.round((done / total) * 100) : 0;

    return (
      <div className="d-flex flex-column align-items-center justify-content-center w-100 px-3">
        <div className="d-flex justify-content-between w-100 mb-1" style={{ fontSize: '13px' }}>
          <span className="text-success font-weight-bold" title="Hoàn thành"><i className="fas fa-check-circle mr-1"></i>{done}</span>
          <span className="text-danger font-weight-bold" title="Đang thực hiện"><i className="fas fa-spinner mr-1"></i>{doing}</span>
        </div>
        <div className="progress w-100 shadow-sm" style={{ height: '6px', backgroundColor: '#e9ecef', borderRadius: '3px' }}>
          <div className="progress-bar bg-success" role="progressbar" style={{ width: `${pct}%` }} aria-valuenow={pct} aria-valuemin="0" aria-valuemax="100"></div>
          <div className="progress-bar bg-danger" role="progressbar" style={{ width: `${100 - pct}%` }} aria-valuenow={100 - pct} aria-valuemin="0" aria-valuemax="100"></div>
        </div>
      </div>
    );
  };

  return (
    <Card id="bao-cao-tong-hop-nhan-vien">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <div className="w-100">
          <h3 className="card-title mb-0 float-none">Báo cáo chi tiết tất cả nhân viên</h3>
        </div>
      </Card.Header>

      <Card.Body>
        {/* ROW 1: CARDS */}
        <Row className="mb-3">
          <Col lg={3} xs={6}>
            <div className="small-box bg-indigo">
              <div className="inner">
                <h3>{dashboardStats.totalEmployees}</h3>
                <p>Tổng thành viên</p>
              </div>
              <div className="icon">
                <i className="fas fa-users" />
              </div>
            </div>
          </Col>
          <Col lg={3} xs={6}>
            <div className="small-box bg-info">
              <div className="inner">
                <h3>{dashboardStats.totalTasks}</h3>
                <p>Tổng công việc</p>
              </div>
              <div className="icon">
                <i className="fas fa-tasks" />
              </div>
            </div>
          </Col>
          <Col lg={3} xs={6}>
            <div className="small-box bg-success">
              <div className="inner">
                <h3>{dashboardStats.totalDone}</h3>
                <p>Đã hoàn thành</p>
              </div>
              <div className="icon">
                <i className="fas fa-check-circle" />
              </div>
            </div>
          </Col>
          <Col lg={3} xs={6}>
            <div className="small-box bg-danger">
              <div className="inner">
                <h3>{dashboardStats.progress}<sup style={{ fontSize: '20px' }}>%</sup></h3>
                <p>Tiến độ ({MODULES.find(m => m.value === moduleFilter)?.label})</p>
              </div>
              <div className="icon">
                <i className="fas fa-chart-line" />
              </div>
            </div>
          </Col>
        </Row>

        {/* ROW 2: FILTERS */}
        <div className="d-flex flex-wrap align-items-end gap-2 mb-3">
          <div style={{ minWidth: 220 }}>
            <label className="form-label mb-1">Khoảng thời gian</label>
            <select
              className="form-select form-select-sm"
              value={rangeLabel}
              onChange={handleChangeRange}
              disabled={loadingTongHopNhanVien}
            >
              <option value="">-- Chọn --</option>
              {RANGE_OPTIONS.map((x) => (
                <option key={x} value={x}>
                  {x}
                </option>
              ))}
            </select>
          </div>

          <div style={{ minWidth: 220 }}>
            <label className="form-label mb-1">Nhóm</label>
            <select
              className="form-select form-select-sm"
              value={teamFilter}
              onChange={handleChangeTeamFilter}
              disabled={loadingTongHopNhanVien}
            >
              <option value="">-- Tất cả nhóm --</option>
              {teamOptions.map((x) => (
                <option key={x.value} value={x.value}>
                  {x.label}
                </option>
              ))}
            </select>
          </div>

          <div style={{ minWidth: 220 }}>
            <label className="form-label mb-1">Module / Loại công việc</label>
            <select
              className="form-select form-select-sm"
              value={moduleFilter}
              onChange={(e) => {
                setModuleFilter(e.target.value);
                updateModuleFilterQuery(e.target.value);
              }}
              disabled={loadingTongHopNhanVien}
            >
              {MODULES.map((x) => (
                <option key={x.value} value={x.value}>
                  {x.label}
                </option>
              ))}
            </select>
          </div>

          {rangeLabel === RANGE_CUSTOM && (
            <>
              <div>
                <label className="form-label mb-1">Từ ngày</label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  disabled={loadingTongHopNhanVien}
                />
              </div>

              <div>
                <label className="form-label mb-1">Đến ngày</label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  disabled={loadingTongHopNhanVien}
                />
              </div>

              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={handleApplyCustomRange}
                disabled={loadingTongHopNhanVien}
              >
                Lọc
              </button>
            </>
          )}

          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={handleClearFilter}
            disabled={loadingTongHopNhanVien}
          >
            Xóa lọc
          </button>

          {(appliedFrom || appliedTo) && (
            <div className="filter-hint" style={{ width: '100%' }}>
              Đang lọc: {fmtDDMMYYYY(appliedFrom) || '...'} → {fmtDDMMYYYY(appliedTo) || '...'}
            </div>
          )}
        </div>

        {/* ROW 3: SOFTWARE HIGHLIGHT (if module = PHAN_MEM) */}
        {moduleFilter === 'PHAN_MEM' && (
          <Row className="mb-4">
            <Col md={12}>
              <Card>
                <Card.Header>
                  <h3 className="card-title">Báo cáo các phần mềm thay đổi</h3>
                </Card.Header>
                <Card.Body>
                  {loadingChiTietPhatTrienPhanMem ? (
                    <div className="d-flex justify-content-center">
                      <Spinner animation="border" size="sm" />
                      <span className="ml-2">Đang tải dữ liệu...</span>
                    </div>
                  ) : (
                    <Row>
                      <Col md={6}>
                        <div className="callout callout-success">
                          <h5>Phần mềm đã hoàn thành ({chiTietPhatTrienPhanMem?.daHoanThanh?.length || 0})</h5>
                          <ul className="list-unstyled mb-0" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                            {chiTietPhatTrienPhanMem?.daHoanThanh?.map((pm, idx) => (
                              <li key={idx} className="mb-2 d-flex justify-content-between align-items-center pr-2">
                                <div>
                                  <strong>{pm.tenPhanMem || 'Không xác định'}</strong>
                                  <br />
                                  <span className="text-muted"><i className="fas fa-users"></i> {pm.nhomThucHien || 'Chưa gán nhóm'}</span>
                                </div>
                                <NavLink
                                  to={`${URI_INTERNAL.PROJECT_DETAIL_PATH}?id=${pm.projectId}&returnUrl=${returnUrl}`}
                                  className="btn btn-sm btn-outline-primary"
                                  title="Xem chi tiết dự án"
                                >
                                  <i className="fas fa-eye"></i>
                                </NavLink>
                              </li>
                            ))}
                            {(!chiTietPhatTrienPhanMem?.daHoanThanh || chiTietPhatTrienPhanMem.daHoanThanh.length === 0) && (
                              <li className="text-muted font-italic">Không có dữ liệu.</li>
                            )}
                          </ul>
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="callout callout-warning">
                          <h5>Phần mềm đang phát triển gần đây({chiTietPhatTrienPhanMem?.dangThucHien?.length || 0})</h5>
                          <ul className="list-unstyled mb-0" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                            {chiTietPhatTrienPhanMem?.dangThucHien?.map((pm, idx) => (
                              <li key={idx} className="mb-2 d-flex justify-content-between align-items-center pr-2">
                                <div>
                                  <strong>{pm.tenPhanMem || 'Không xác định'}</strong>
                                  <br />
                                  <span className="text-muted"><i className="fas fa-users"></i> {pm.nhomThucHien || 'Chưa gán nhóm'}</span>
                                </div>
                                <NavLink
                                  to={`${URI_INTERNAL.PROJECT_DETAIL_PATH}?id=${pm.projectId}&returnUrl=${returnUrl}`}
                                  className="btn btn-sm btn-outline-primary"
                                  title="Xem chi tiết dự án"
                                >
                                  <i className="fas fa-eye"></i>
                                </NavLink>
                              </li>
                            ))}
                            {(!chiTietPhatTrienPhanMem?.dangThucHien || chiTietPhatTrienPhanMem.dangThucHien.length === 0) && (
                              <li className="text-muted font-italic">Không có dữ liệu.</li>
                            )}
                          </ul>
                        </div>
                      </Col>
                    </Row>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        {/* ROW 4: DATA TABLE */}
        {loadingTongHopNhanVien ? (
          <div className="d-flex align-items-center justify-content-center p-4">
            <Spinner animation="border" variant="primary" />
            <span className="ml-3 font-weight-bold text-muted">Đang tải dữ liệu...</span>
          </div>
        ) : (
          <div className="table-responsive">
            <Table
              striped
              bordered
              hover
              className="text-center align-middle w-100 mb-0"
            >
              <thead>
                <tr>
                  <th>STT</th>
                  <th className="text-left">Thành viên</th>

                  {moduleFilter === 'ALL' && (
                    <>
                      <th className="text-primary">Tổng việc</th>
                      <th>Phần mềm</th>
                      <th>Xử lý yêu cầu</th>
                      <th>Hỗ trợ</th>
                      <th>Nhiệm vụ khác</th>
                    </>
                  )}

                  {moduleFilter === 'PHAN_MEM' && (
                    <>
                      <th className="text-primary">Tổng việc PM</th>
                      <th style={{ width: '400px' }}>Trạng thái Phần mềm</th>
                    </>
                  )}

                  {moduleFilter === 'XU_LY_YEU_CAU' && (
                    <>
                      <th className="text-primary">Tổng Yêu Cầu</th>
                      <th style={{ width: '400px' }}>Trạng thái Xử lý YC</th>
                    </>
                  )}

                  {moduleFilter === 'HO_TRO' && (
                    <>
                      <th className="text-primary">Tổng Hỗ trợ</th>
                      <th style={{ width: '400px' }}>Trạng thái Hỗ trợ</th>
                    </>
                  )}

                  {moduleFilter === 'NHIEM_VU_KHAC' && (
                    <>
                      <th className="text-primary">Tổng NV Khác</th>
                      <th style={{ width: '400px' }}>Trạng thái NV Khác</th>
                    </>
                  )}

                  <th>Chức năng</th>
                </tr>
              </thead>

              {filteredRows.length > 0 ? (
                <tbody>
                  {filteredRows.map((r, idx) => (
                    <tr key={`${r.nhanVienId}-${idx}`}>
                      <td className="font-weight-bold text-muted">{idx + 1}</td>

                      <td className="text-left">
                        <div className="font-weight-bold text-dark" style={{ fontSize: '15px' }}>{r.tenThanhVien}</div>
                        <div className="small text-muted mt-1"><i className="fas fa-users text-info mr-1"></i> {r.tenTeam || 'Chưa gán'}</div>
                      </td>

                      {moduleFilter === 'ALL' && (
                        <>
                          <td className="text-primary font-weight-bold" style={{ fontSize: '1.2rem' }}>{r.tongCongViec}</td>
                          <td>{renderModuleStatusCell(r.phanMemHoanThanh, r.phanMemDangThucHien)}</td>
                          <td>{renderModuleStatusCell(r.xuLyYeuCauHoanThanh, r.xuLyYeuCauDangThucHien)}</td>
                          <td>{renderModuleStatusCell(r.hoTroHoanThanh, r.hoTroDangThucHien)}</td>
                          <td>{renderModuleStatusCell(r.nhiemVuKhacHoanThanh, r.nhiemVuKhacDangThucHien)}</td>
                        </>
                      )}

                      {moduleFilter === 'PHAN_MEM' && (
                        <>
                          <td className="text-primary font-weight-bold" style={{ fontSize: '1.2rem' }}>{r.phanMem}</td>
                          <td>{renderModuleStatusCell(r.phanMemHoanThanh, r.phanMemDangThucHien)}</td>
                        </>
                      )}

                      {moduleFilter === 'XU_LY_YEU_CAU' && (
                        <>
                          <td className="text-primary font-weight-bold" style={{ fontSize: '1.2rem' }}>{r.xuLyYeuCau}</td>
                          <td>{renderModuleStatusCell(r.xuLyYeuCauHoanThanh, r.xuLyYeuCauDangThucHien)}</td>
                        </>
                      )}

                      {moduleFilter === 'HO_TRO' && (
                        <>
                          <td className="text-primary font-weight-bold" style={{ fontSize: '1.2rem' }}>{r.hoTro}</td>
                          <td>{renderModuleStatusCell(r.hoTroHoanThanh, r.hoTroDangThucHien)}</td>
                        </>
                      )}

                      {moduleFilter === 'NHIEM_VU_KHAC' && (
                        <>
                          <td className="text-primary font-weight-bold" style={{ fontSize: '1.2rem' }}>{r.nhiemVuKhac}</td>
                          <td>{renderModuleStatusCell(r.nhiemVuKhacHoanThanh, r.nhiemVuKhacDangThucHien)}</td>
                        </>
                      )}

                      <td>
                        <ul className="nav justify-content-center">
                          <li className="nav-item">
                            <NavLink
                              to={`${detailPath}?nhanVienId=${r.nhanVienId}${r.teamId != null && r.teamId !== '' ? `&teamId=${r.teamId}` : ''
                                }${appliedFrom ? `&From=${encodeURIComponent(appliedFrom)}` : ''}${appliedTo ? `&To=${encodeURIComponent(appliedTo)}` : ''
                                }&source=tong-hop-tat-ca&sourceTab=bao-cao-nhan-vien&returnUrl=${returnUrl}`}
                              exact
                              className="nav-link"
                              title="Xem chi tiết"
                            >
                              <i className="nav-icon font-icon fas fa-eye" />
                            </NavLink>
                          </li>
                        </ul>
                      </td>
                    </tr>
                  ))}
                </tbody>
              ) : (
                <tbody>
                  <tr>
                    <td colSpan={moduleFilter === 'ALL' ? "8" : "5"} className="text-center py-5">
                      <div className="text-muted">
                        <i className="fas fa-inbox fa-3x mb-3 opacity-50"></i>
                        <h5>Không có dữ liệu</h5>
                        <p>Vui lòng thay đổi bộ lọc để xem kết quả</p>
                      </div>
                    </td>
                  </tr>
                </tbody>
              )}
            </Table>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default BaoCaoTongHopNhanVien;